import React, {useState} from 'react';
import {Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, IconButton,
    LinearProgress, MenuItem, TextField, useMediaQuery, useTheme} from '@mui/material';
import {Close} from '@mui/icons-material';
import {connect} from 'react-redux';
import {Controller, useForm} from 'react-hook-form';
import {columnActions} from '../../store/actions/columnAction';
import {ColumnDialog, ColumnSection} from '../reporting-table/ColumnDesign';
import {SettingsDialogDescription} from '../settings/SettingsDialog';
import {getSettingsColors} from '../settings/SettingsDesign';
import FormulaInput from './FormulaInput';

const defaults = {name: '', description: '', formula: '', format: 'number', decimals: 0};

export function AddCustomColumn({onClose, open, createCustomColumn, state}) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const titleId = React.useId();
    const formId = React.useId();
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');
    const {register, handleSubmit, setValue, watch, control, formState: {errors}, reset} = useForm({defaultValues: defaults});

    const handleClose = (event, reason) => {
        if (reason === 'backdropClick' || loading) return;
        reset(defaults);
        setRequestError('');
        onClose();
    };
    const onSubmit = async data => {
        if (loading) return;
        setLoading(true);
        setRequestError('');
        try {
            await createCustomColumn(data);
            reset(defaults);
            onClose();
        } catch (error) {
            setRequestError(error?.response?.data?.error || 'Unable to create your custom column. Please try again.');
        } finally {
            setLoading(false);
        }
    };
    const menuProps = {slotProps: {paper: {sx: {bgcolor: theme => theme.palette.mode === 'light'
        ? theme.palette.background.paper : getSettingsColors(theme).input, borderRadius: '10px',
        '& .MuiMenuItem-root': {fontSize: 13, minHeight: 36}}}}};

    return <ColumnDialog open={open} onClose={handleClose} fullWidth fullScreen={fullScreen} maxWidth="md" aria-labelledby={titleId}>
        <DialogTitle id={titleId}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                <Box component="span">New custom column</Box>
                <IconButton aria-label="Close custom column" size="small" disabled={loading} onClick={handleClose}><Close fontSize="small"/></IconButton>
            </Box>
            <SettingsDialogDescription>Build a calculated metric for your reports using existing metrics.</SettingsDialogDescription>
        </DialogTitle>
        <DialogContent>
            {loading && <LinearProgress sx={{position: 'absolute', top: 0, left: 0, right: 0}}/>}
            {requestError && <Alert severity="error" sx={{mb: 2}}>{requestError}</Alert>}
            <Box component="form" id={formId} onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate sx={{display: 'grid', gap: 2}}>
                <Box component="fieldset" disabled={loading} sx={{border: 0, p: 0, m: 0, minWidth: 0, display: 'grid', gap: 2}}>
                    <ColumnSection title="Column details">
                        <Box sx={{display: 'grid', gap: 2}}>
                            <TextField label="Column name" required disabled={loading} size="small" fullWidth placeholder="e.g. Cost per purchase" error={!!errors.name}
                                helperText={errors.name?.message} slotProps={{inputLabel: {shrink: true}}}
                                {...register('name', {required: 'Enter a column name.', minLength: {value: 2, message: 'Use at least 2 characters.'},
                                    maxLength: {value: 100, message: 'Use 100 characters or fewer.'},
                                    pattern: {value: /^[a-zA-Z\s_-]+$/, message: 'Use letters, spaces, hyphens or underscores.'}})}/>
                            <TextField label="Description" required disabled={loading} size="small" fullWidth placeholder="Explain what this metric measures"
                                error={!!errors.description} helperText={errors.description?.message} slotProps={{inputLabel: {shrink: true}}}
                                {...register('description', {required: 'Enter a description.', minLength: {value: 2, message: 'Use at least 2 characters.'},
                                    maxLength: {value: 250, message: 'Use 250 characters or fewer.'}})}/>
                        </Box>
                    </ColumnSection>
                    <ColumnSection title="Calculation" description="Insert metrics and operators, or type your formula below.">
                        <FormulaInput setValue={setValue} errors={!!errors.formula} name="formula" label="Formula"
                            register={register} watch={watch} errorMessage={errors.formula?.message}
                            columns={state?.column?.data?.campaigns} disabled={loading}/>
                    </ColumnSection>
                    <ColumnSection title="Display format" description="Choose how this metric appears in your report.">
                        <Box sx={{display: 'grid', gridTemplateColumns: {xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))'}, gap: 2}}>
                            <Controller name="format" control={control} render={({field}) => <TextField {...field} disabled={loading} select fullWidth size="small"
                                label="Format" slotProps={{inputLabel: {shrink: true}, select: {MenuProps: menuProps}}}>
                                <MenuItem value="number">Number</MenuItem><MenuItem value="percent">Percent</MenuItem><MenuItem value="currency">Currency</MenuItem>
                            </TextField>}/>
                            <Controller name="decimals" control={control} render={({field}) => <TextField {...field} disabled={loading} select fullWidth size="small"
                                label="Decimal places" slotProps={{inputLabel: {shrink: true}, select: {MenuProps: menuProps}}}>
                                {[0, 1, 2, 3, 4].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                            </TextField>}/>
                        </Box>
                    </ColumnSection>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button color="inherit" onClick={handleClose} disabled={loading}>Cancel</Button>
            <Button type="submit" form={formId} variant="contained" disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit"/> : undefined}>{loading ? 'Creating…' : 'Create column'}</Button>
        </DialogActions>
    </ColumnDialog>;
}

export default connect(state => ({state: {column: state.column}}), {createCustomColumn: columnActions.createCustomColumn})(AddCustomColumn);
